import { ProductCodeKey } from '@dayetopia/library';
import { AppState } from '@constants';
import { CartItem, CartItemType, ShippingFees } from '@contracts';

interface ShippingCostProps {
  price: number;
  freeShippingRequirement: number;
  cartItems: CartItem[];
  shippingFees: Partial<ShippingFees>;
}

/**
 * Gets the shipping fee for a product with fallback to default
 * @param shippingFees - The shipping fees object
 * @param productCode - The product code to look up
 * @returns The shipping fee object with shippingPrice and originalShippingPrice
 */
function getShippingFeeForProduct(shippingFees: Partial<ShippingFees>, productCode: ProductCodeKey) {
  return shippingFees[productCode] ?? shippingFees.default ?? { shippingPrice: 0, originalShippingPrice: 0 };
}

export function calculateShippingCost({ price, freeShippingRequirement, cartItems, shippingFees }: ShippingCostProps) {
  // Check for special cases that have fixed shipping rules
  const specialCaseResult = handleSpecialCases(cartItems, shippingFees, price, freeShippingRequirement);
  if (specialCaseResult) {
    return specialCaseResult;
  }

  // When price is zero or negative (e.g., 100% coupon), shipping is free
  if (price <= 0) {
    const { subscriptionItems: subItems, oneOffItems: offItems } = categorizeCartItems(cartItems);
    const originalShippingPrice = calculateOriginalShippingPrice(subItems, offItems, shippingFees);
    updateSubscriptionItemsShippingFee(subItems, true, shippingFees);
    return { originalShippingPrice, shippingPrice: 0 };
  }

  // Get items relevant for shipping calculation (excluding free/dependent items)
  const { subscriptionItems, oneOffItems } = categorizeCartItems(cartItems);

  // Calculate the original shipping price (for display purposes)
  const originalShippingPrice = calculateOriginalShippingPrice(subscriptionItems, oneOffItems, shippingFees);

  // If price exceeds free shipping threshold, shipping is free
  if (price > freeShippingRequirement) {
    // Update subscription items with shipping fee values (set to 0 for free shipping)
    updateSubscriptionItemsShippingFee(subscriptionItems, true, shippingFees);
    return { originalShippingPrice, shippingPrice: 0 };
  }

  // Update subscription items with shipping fee values
  updateSubscriptionItemsShippingFee(subscriptionItems, false, shippingFees);

  // Calculate final shipping price
  const shippingPrice = calculateFinalShippingPrice(subscriptionItems, oneOffItems, shippingFees);

  return { originalShippingPrice, shippingPrice };
}

/**
 * Handles special shipping cases that have priority rules
 */
function handleSpecialCases(cartItems: CartItem[], shippingFees: Partial<ShippingFees>, price?: number, freeShippingRequirement?: number) {
  // If price meets free shipping requirement, return free shipping regardless of special cases
  if (price !== undefined && freeShippingRequirement !== undefined && price > freeShippingRequirement) {
    // Get original shipping price for reference - use the same logic as calculateOriginalShippingPrice
    // Instead of summing all shipping fees, we'll use the max fee
    const allFees: number[] = [];
    cartItems.forEach((item) => {
      if (item.price === 0 || item.id.includes('_free')) return; // Skip free items
      const fee = getShippingFeeForProduct(shippingFees, item.productCode).originalShippingPrice;
      if (fee > 0) {
        allFees.push(fee);
      }
    });
    const originalShippingPrice = allFees.length ? Math.max(...allFees) : 0;
    return { originalShippingPrice, shippingPrice: 0 };
  }

  const hasSpecialCase = {
    singleFreeItem: cartItems.length === 1 && cartItems[0].price === 0,
    oneOffTamponBox: cartItems.some((item) => item.productCode === ProductCodeKey.TamponBox && item.type === CartItemType.OneOffPurchase),
    oneOffProviotics: cartItems.some((item) => item.productCode === ProductCodeKey.Proviotics && item.type === CartItemType.OneOffPurchase)
  };

  if (hasSpecialCase.singleFreeItem) {
    const originalShippingPrice = cartItems.reduce(
      (total, item) => total + getShippingFeeForProduct(shippingFees, item.productCode).originalShippingPrice,
      0
    );
    return { originalShippingPrice, shippingPrice: originalShippingPrice };
  }

  // For mixed carts with special items like Proviotics, use standard shipping calculation
  if (cartItems.length > 1 && (hasSpecialCase.oneOffTamponBox || hasSpecialCase.oneOffProviotics)) {
    return null;
  }

  // Only apply special case for single items
  if (hasSpecialCase.oneOffTamponBox || hasSpecialCase.oneOffProviotics) {
    const originalShippingPrice = cartItems.reduce(
      (total, item) => total + getShippingFeeForProduct(shippingFees, item.productCode).originalShippingPrice,
      0
    );
    return { originalShippingPrice, shippingPrice: originalShippingPrice };
  }

  return null;
}

/**
 * Categorizes cart items into subscription and one-off items, excluding free and dependent items
 */
function categorizeCartItems(cartItems: CartItem[]) {
  // Identify items to exclude from shipping calculation
  const itemsToExclude = new Set([
    // Free items
    ...cartItems.filter((item) => item.price === 0 || item.id.includes('_free')).map((item) => item.id),

    // Dependent items
    ...cartItems
      .filter((item) => item.dependantCartItems && item.dependantCartItems.length > 0)
      .flatMap((item) => item.dependantCartItems?.map((dep) => dep.id) || [])
  ]);

  // Split items by type
  const subscriptionItems = cartItems.filter((item) => item.type === CartItemType.Subscription);
  const oneOffItems = cartItems.filter(
    (item) => item.type === CartItemType.OneOffPurchase && !itemsToExclude.has(item.id) && !item.id.includes('_free')
  );

  return { subscriptionItems, oneOffItems };
}

/**
 * Calculates the original shipping price for display purposes
 */
function calculateOriginalShippingPrice(subscriptionItems: CartItem[], oneOffItems: CartItem[], shippingFees: Partial<ShippingFees>) {
  // Get the shipping fees for all items (subscription and one-off)
  const allShippingFees: number[] = [];

  // Add subscription shipping fees
  subscriptionItems.forEach((item) => {
    const fee = getShippingFeeForProduct(shippingFees, item.productCode).originalShippingPrice;
    if (fee > 0) {
      allShippingFees.push(fee);
    }
  });

  // Add one-off shipping fees
  oneOffItems.forEach((item) => {
    const fee = getShippingFeeForProduct(shippingFees, item.productCode).originalShippingPrice;
    if (fee > 0) {
      allShippingFees.push(fee);
    }
  });

  // If we have multiple subscription items, only charge one shipping fee
  // For display purposes, show what the shipping would be if each item had its normal fee
  if (subscriptionItems.length > 1) {
    return Math.max(...allShippingFees) * allShippingFees.length;
  }

  // For mixed carts or carts with a single item, use the maximum shipping fee
  return allShippingFees.length ? Math.max(...allShippingFees) : 0;
}

/**
 * Updates subscription items with the appropriate shipping fee
 */
function updateSubscriptionItemsShippingFee(subscriptionItems: CartItem[], isFreeShipping: boolean, shippingFees: Partial<ShippingFees>) {
  subscriptionItems.forEach((item) => {
    const shippingFee = isFreeShipping ? 0 : getShippingFeeForProduct(shippingFees, item.productCode).shippingPrice;
    if (item.shippingFee !== shippingFee) {
      Object.assign(item, { shippingFee });
    }
  });
}

/**
 * Calculates the final shipping price based on subscription and one-off items
 */
function calculateFinalShippingPrice(subscriptionItems: CartItem[], oneOffItems: CartItem[], shippingFees: Partial<ShippingFees>) {
  // Collect all shipping fees
  const allShippingFees: number[] = [];

  // For subscription items, only charge the maximum shipping fee
  if (subscriptionItems.length > 0) {
    const subscriptionFees = subscriptionItems.map((item) => getShippingFeeForProduct(shippingFees, item.productCode).shippingPrice);
    const maxSubscriptionFee = Math.max(...subscriptionFees, 0);
    if (maxSubscriptionFee > 0) {
      allShippingFees.push(maxSubscriptionFee);
    }
  }

  // Add shipping fees from one-off items
  oneOffItems.forEach((item) => {
    const fee = getShippingFeeForProduct(shippingFees, item.productCode).shippingPrice;
    if (fee > 0) {
      allShippingFees.push(fee);
    }
  });

  // For all mixed carts, use the maximum shipping fee
  return allShippingFees.length ? Math.max(...allShippingFees) : 0;
}

/**
 * Calculates the shipping cost value based on the state.
 * @param {AppState} state - The state of the app.
 * @returns {number} The shipping cost value.
 */
export function getShippingCostValue(state: AppState) {
  const { price, freeShippingRequirement, cartItems, shippingFees } = state;
  return calculateShippingCost({ price, freeShippingRequirement, cartItems, shippingFees });
}
